import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { warehousesApi, inventoryItemsApi } from '../../api/warehouse';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';

interface CreateWarehouseForm { name: string; location: string; managerEmployeeId?: number; }

export function WarehousesListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateWarehouseForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['warehouses'], queryFn: () => warehousesApi.list() });
  const { data: lowStockData } = useQuery({ queryKey: ['inventory-low-stock'], queryFn: () => inventoryItemsApi.getLowStock() });

  const createMutation = useMutation({
    mutationFn: (input: CreateWarehouseForm) => warehousesApi.create({ ...input }),
    onSuccess: (result) => {
      showSuccess(`Warehouse ${result.data.warehouseReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/warehouses/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const warehouses = data?.data ?? [];
  const lowStockItems = lowStockData?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Warehouses / Inventory" description="Warehouse register, stock levels, goods receipt, and stock requests." />

      {lowStockItems.length > 0 && (
        <div className="warning-state">
          {lowStockItems.length} item{lowStockItems.length === 1 ? '' : 's'} at or below reorder level: {lowStockItems.map((i) => i.name).join(', ')}
        </div>
      )}

      <SectionHeader title="Warehouses" actions={
        <>
          <a onClick={() => navigate('/logistics/item-catalogue')} className="table-link">View Item Catalogue →</a>
          <a onClick={() => navigate('/logistics/goods-receipts')} className="table-link">View Goods Receipts →</a>
          <a onClick={() => navigate('/logistics/stock-requests')} className="table-link">View Stock Requests →</a>
          <a onClick={() => navigate('/logistics/stock-adjustments')} className="table-link">View Stock Adjustments →</a>
          <a onClick={() => navigate('/logistics/stock-counts')} className="table-link">View Stock Counts →</a>
          <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'Register Warehouse'}</button>
        </>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Name <input {...register('name', { required: true })} /></label>
          <label>Location <input {...register('location', { required: true })} /></label>
          <label>Manager <EmployeePicker {...register('managerEmployeeId', { valueAsNumber: true })} /></label>
          <button type="submit" disabled={createMutation.isPending}>Register</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load warehouses. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={warehouses}
          rowKey={(w) => w.id}
          columns={[
            { header: 'Reference', render: (w) => <a onClick={() => navigate(`/logistics/warehouses/${w.id}`)} className="table-link">{w.warehouse_reference}</a> },
            { header: 'Name', render: (w) => w.name },
            { header: 'Location', render: (w) => w.location },
            { header: 'Status', render: (w) => <StatusBadge status={w.is_active ? 'active' : 'inactive'} /> },
          ]}
        />
      )}
    </div>
  );
}
